import { type ComponentProps } from 'react';

import {
	Progress as ArkProgress,
	type ProgressRootProps as ArkProgressRootProps
} from '@ark-ui/react';

import type { ActionColor, Radius, Scale3 } from '../../lib/types';
import { cn } from '../../lib/utils';

type ProgressProps = Omit<ArkProgressRootProps, 'color'> & {
	size?: Scale3;
	variant?: 'classic' | 'surface' | 'soft';
	color?: ActionColor;
	highContrast?: boolean;
	radius?: Radius;
};

function ProgressRoot({
	className,
	size = '2',
	variant = 'surface',
	color = 'accent',
	highContrast,
	radius,
	children,
	...props
}: ProgressProps) {
	return (
		<ArkProgress.Root
			className={cn('pt-progress', className)}
			data-size={size}
			data-variant={variant}
			data-accent-color={color}
			data-high-contrast={highContrast ? '' : undefined}
			data-radius={radius}
			{...props}
		>
			{children}
		</ArkProgress.Root>
	);
}

function ProgressTrack({ className, ...props }: ComponentProps<typeof ArkProgress.Track>) {
	return <ArkProgress.Track className={cn('pt-progress-track', className)} {...props} />;
}

function ProgressRange({ className, ...props }: ComponentProps<typeof ArkProgress.Range>) {
	return <ArkProgress.Range className={cn('pt-progress-range', className)} {...props} />;
}

function ProgressLabel({ className, ...props }: ComponentProps<typeof ArkProgress.Label>) {
	return <ArkProgress.Label className={cn('pt-progress-label', className)} {...props} />;
}

function ProgressValueText({ className, ...props }: ComponentProps<typeof ArkProgress.ValueText>) {
	return <ArkProgress.ValueText className={cn('pt-progress-value-text', className)} {...props} />;
}

const Progress = Object.assign(ProgressRoot, {
	Root: ProgressRoot,
	Track: ProgressTrack,
	Range: ProgressRange,
	Label: ProgressLabel,
	ValueText: ProgressValueText
});

export { Progress, type ProgressProps };
